type SpacePosition = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type SharedBorderOffsets = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type HorizontalBorderOffsets = Pick<
  SharedBorderOffsets,
  'left' | 'right'
>;
export type VerticalBorderOffsets = Pick<SharedBorderOffsets, 'top' | 'bottom'>;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const MERGE_EPSILON = 0.5;

export const getSharedHorizontalBorderOffsets = (
  spacePosition: SpacePosition,
  adjacentPosition: SpacePosition
): HorizontalBorderOffsets => {
  const overlapStart = Math.max(spacePosition.x, adjacentPosition.x);
  const overlapEnd = Math.min(
    spacePosition.x + spacePosition.width,
    adjacentPosition.x + adjacentPosition.width
  );

  const left = clamp(overlapStart - spacePosition.x, 0, spacePosition.width);
  const right = clamp(
    spacePosition.x + spacePosition.width - overlapEnd,
    0,
    spacePosition.width
  );

  return { left, right };
};

export const getSharedVerticalBorderOffsets = (
  spacePosition: SpacePosition,
  adjacentPosition: SpacePosition
): VerticalBorderOffsets => {
  const overlapStart = Math.max(spacePosition.y, adjacentPosition.y);
  const overlapEnd = Math.min(
    spacePosition.y + spacePosition.height,
    adjacentPosition.y + adjacentPosition.height
  );

  const top = clamp(overlapStart - spacePosition.y, 0, spacePosition.height);
  const bottom = clamp(
    spacePosition.y + spacePosition.height - overlapEnd,
    0,
    spacePosition.height
  );

  return { top, bottom };
};

export const getMergedHorizontalBorderOffsets = (
  spacePosition: SpacePosition,
  adjacentPositions: SpacePosition[]
): HorizontalBorderOffsets[] => {
  const intervals = adjacentPositions
    .map((adjacentPosition) =>
      getSharedHorizontalBorderOffsets(spacePosition, adjacentPosition)
    )
    .map((offsets) => ({
      start: offsets.left,
      end: spacePosition.width - offsets.right
    }))
    .filter((interval) => interval.end > interval.start)
    .sort((a, b) => a.start - b.start);

  if (intervals.length === 0) {
    return [];
  }

  const mergedIntervals = [intervals[0]];

  for (let i = 1; i < intervals.length; i += 1) {
    const interval = intervals[i];
    const lastMergedInterval = mergedIntervals[mergedIntervals.length - 1];

    if (interval.start <= lastMergedInterval.end + MERGE_EPSILON) {
      lastMergedInterval.end = Math.max(lastMergedInterval.end, interval.end);
    } else {
      mergedIntervals.push(interval);
    }
  }

  return mergedIntervals.map((interval) => ({
    left: clamp(interval.start, 0, spacePosition.width),
    right: clamp(spacePosition.width - interval.end, 0, spacePosition.width)
  }));
};

export const getMergedVerticalBorderOffsets = (
  spacePosition: SpacePosition,
  adjacentPositions: SpacePosition[]
): VerticalBorderOffsets[] => {
  const intervals = adjacentPositions
    .map((adjacentPosition) =>
      getSharedVerticalBorderOffsets(spacePosition, adjacentPosition)
    )
    .map((offsets) => ({
      start: offsets.top,
      end: spacePosition.height - offsets.bottom
    }))
    .filter((interval) => interval.end > interval.start)
    .sort((a, b) => a.start - b.start);

  if (intervals.length === 0) {
    return [];
  }

  const mergedIntervals = [intervals[0]];

  for (let i = 1; i < intervals.length; i += 1) {
    const interval = intervals[i];
    const lastMergedInterval = mergedIntervals[mergedIntervals.length - 1];

    if (interval.start <= lastMergedInterval.end + MERGE_EPSILON) {
      lastMergedInterval.end = Math.max(lastMergedInterval.end, interval.end);
    } else {
      mergedIntervals.push(interval);
    }
  }

  return mergedIntervals.map((interval) => ({
    top: clamp(interval.start, 0, spacePosition.height),
    bottom: clamp(spacePosition.height - interval.end, 0, spacePosition.height)
  }));
};
